import { useForm, Link } from "@inertiajs/react";
import { useEffect, useState } from "react";
import AuthCard from "@/components/AuthCard";
import GoogleButton from "@/components/GoogleButton";
import { countUnsentOps } from "@/lib/localDb";
import { changeCount } from "@/lib/plural";

function useUnsentOps(): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let current = true;
    void countUnsentOps().then((n) => current && setCount(n));
    return () => {
      current = false;
    };
  }, []);
  return count;
}

interface Props {
  flash?: { alert?: string; notice?: string };
  csrfToken: string;
}

export default function Login({ flash, csrfToken }: Props) {
  const { data, setData, post, processing, errors: rawErrors } = useForm({
    user: { email: "", password: "" },
  });
  const errors = rawErrors as Partial<Record<string, string>>;

  const [showPassword, setShowPassword] = useState(false);
  const unsent = useUnsentOps();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    post("/users/sign_in");
  }

  return (
    <AuthCard title="revolver" subtitle="Войди, чтобы продолжить">
      {unsent > 0 && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
          Войдите, чтобы отправить {changeCount(unsent)}
        </p>
      )}
      {flash?.alert && (
        <p className="text-sm text-red-600 mb-4">{flash.alert}</p>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Эл. почта
          </label>
          <input
            type="email"
            value={data.user.email}
            onChange={(e) => setData("user", { ...data.user, email: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
            required
          />
          {errors["user.email"] && (
            <p className="text-xs text-red-600 mt-1">{errors["user.email"]}</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Пароль
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={data.user.password}
              onChange={(e) => setData("user", { ...data.user, password: e.target.value })}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              required
              minLength={8}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500"
              aria-label={showPassword ? "Скрыть пароль" : "Показать пароль"}
            >
              {showPassword ? "Скрыть" : "Показать"}
            </button>
          </div>
          {errors["user.password"] && (
            <p className="text-xs text-red-600 mt-1">{errors["user.password"]}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={processing}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 rounded-lg disabled:opacity-50"
        >
          {processing ? "Входим…" : "Войти"}
        </button>
      </form>

      <div className="my-6 flex items-center gap-3 text-xs text-gray-400">
        <span className="flex-1 h-px bg-gray-200" />
        <span>или</span>
        <span className="flex-1 h-px bg-gray-200" />
      </div>
      <GoogleButton csrfToken={csrfToken} />

      <p className="text-xs text-gray-500 text-center mt-6">
        Нет аккаунта?{" "}
        <Link href="/users/sign_up" className="text-blue-600 hover:underline">
          Зарегистрироваться
        </Link>
      </p>
    </AuthCard>
  );
}
